/**
 * Canonical, index-aligned language names per locale (same convention as
 * lambda/utils/processor/locale-enum-fields.ts: position i means the same language in en/fi/sv),
 * keyed by ISO 639-1 code. Single source of truth reused by:
 * - validate-job-fields.ts, to normalize/validate requiredLanguages_* and optionalLanguages_* and to
 *   translate a value that leaked into the wrong locale (e.g. "Finnish" inside requiredLanguages_sv
 *   should read "Finska", not pass through untranslated).
 * - lambda/utils/tyomarkkinatori-api/normalize-tmt-codes.ts's workLanguageLabel, which previously
 *   kept its own separate copy of this same table.
 */
export const LANGUAGE_NAMES_BY_ISO: Record<string, { en: string; fi: string; sv: string }> = {
  en: { en: 'English', fi: 'Englanti', sv: 'Engelska' },
  fi: { en: 'Finnish', fi: 'Suomi', sv: 'Finska' },
  sv: { en: 'Swedish', fi: 'Ruotsi', sv: 'Svenska' },
  de: { en: 'German', fi: 'Saksa', sv: 'Tyska' },
  fr: { en: 'French', fi: 'Ranska', sv: 'Franska' },
  es: { en: 'Spanish', fi: 'Espanja', sv: 'Spanska' },
  it: { en: 'Italian', fi: 'Italia', sv: 'Italienska' },
  pt: { en: 'Portuguese', fi: 'Portugali', sv: 'Portugisiska' },
  nl: { en: 'Dutch', fi: 'Hollanti', sv: 'Nederländska' },
  ru: { en: 'Russian', fi: 'Venäjä', sv: 'Ryska' },
  pl: { en: 'Polish', fi: 'Puola', sv: 'Polska' },
  cs: { en: 'Czech', fi: 'Tšekki', sv: 'Tjeckiska' },
  hu: { en: 'Hungarian', fi: 'Unkari', sv: 'Ungerska' },
  ro: { en: 'Romanian', fi: 'Romania', sv: 'Rumänska' },
  bg: { en: 'Bulgarian', fi: 'Bulgaria', sv: 'Bulgarianska' },
  hr: { en: 'Croatian', fi: 'Kroatia', sv: 'Kroatiska' },
  sr: { en: 'Serbian', fi: 'Serbia', sv: 'Serbiska' },
  sk: { en: 'Slovak', fi: 'Slovakia', sv: 'Slovakiska' },
  sl: { en: 'Slovenian', fi: 'Slovenia', sv: 'Slovenska' },
  da: { en: 'Danish', fi: 'Tanska', sv: 'Danska' },
  no: { en: 'Norwegian', fi: 'Norja', sv: 'Norska' },
  nb: { en: 'Norwegian', fi: 'Norja', sv: 'Norska' },
  nn: { en: 'Norwegian', fi: 'Norja', sv: 'Norska' },
  is: { en: 'Icelandic', fi: 'Islanti', sv: 'Isländska' },
  et: { en: 'Estonian', fi: 'Viro', sv: 'Estniska' },
  lv: { en: 'Latvian', fi: 'Latvia', sv: 'Lettiska' },
  lt: { en: 'Lithuanian', fi: 'Liettua', sv: 'Litauiska' },
  el: { en: 'Greek', fi: 'Kreikka', sv: 'Grekiska' },
  tr: { en: 'Turkish', fi: 'Turkki', sv: 'Turkiska' },
  ar: { en: 'Arabic', fi: 'Arabia', sv: 'Arabiska' },
  he: { en: 'Hebrew', fi: 'Heprea', sv: 'Hebreiska' },
  zh: { en: 'Chinese', fi: 'Kiina', sv: 'Kinesiska' },
  ja: { en: 'Japanese', fi: 'Japani', sv: 'Japanska' },
  ko: { en: 'Korean', fi: 'Korea', sv: 'Koreanska' },
  hi: { en: 'Hindi', fi: 'Hindi', sv: 'Hindi' },
  th: { en: 'Thai', fi: 'Thai', sv: 'Thai' },
  vi: { en: 'Vietnamese', fi: 'Vietnam', sv: 'Vietnamesiska' },
  id: { en: 'Indonesian', fi: 'Indonesia', sv: 'Indonesiska' },
  ms: { en: 'Malay', fi: 'Malaiji', sv: 'Malajiska' },
};

export type LanguageLocale = 'en' | 'fi' | 'sv';

/** Per-locale set of every canonical language name, for exact/case-insensitive membership checks. */
export const LANGUAGE_NAME_SETS: Record<LanguageLocale, Set<string>> = {
  en: new Set(Object.values(LANGUAGE_NAMES_BY_ISO).map((t) => t.en)),
  fi: new Set(Object.values(LANGUAGE_NAMES_BY_ISO).map((t) => t.fi)),
  sv: new Set(Object.values(LANGUAGE_NAMES_BY_ISO).map((t) => t.sv)),
};

/**
 * Lowercased language name (in ANY of en/fi/sv) → the full {en, fi, sv} triple it belongs to.
 * Lets a value that leaked into the wrong locale (e.g. "Finnish" found inside requiredLanguages_sv,
 * or "Suomi" found inside requiredLanguages_en) resolve to the correct name for whichever locale
 * it's actually needed in, instead of being displayed untranslated.
 */
export const LANGUAGE_NAME_TO_TRIPLE: Map<string, { en: string; fi: string; sv: string }> = (() => {
  const map = new Map<string, { en: string; fi: string; sv: string }>();
  for (const triple of Object.values(LANGUAGE_NAMES_BY_ISO)) {
    map.set(triple.en.toLowerCase(), triple);
    map.set(triple.fi.toLowerCase(), triple);
    map.set(triple.sv.toLowerCase(), triple);
  }
  return map;
})();

/** Levenshtein edit distance, capped: returns Infinity as soon as it's clear the distance exceeds `max`. */
function editDistanceAtMost(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      rowMin = Math.min(rowMin, curr[j]);
    }
    if (rowMin > max) return max + 1;
    prev = curr;
  }
  return prev[b.length];
}

/**
 * Finds a language name in the given locale's own list that `value` is a near-miss of (edit
 * distance <= 1): catches a malformed/wrong grammatical form of a real language name, e.g.
 * Swedish "Finskt" (an adjective ending) instead of the noun "Finska", without opening the door to
 * matching genuinely unrelated single words.
 */
export function findFuzzyLanguageMatch(value: string, locale: LanguageLocale): string | undefined {
  const lower = value.toLowerCase();
  for (const name of LANGUAGE_NAME_SETS[locale]) {
    if (editDistanceAtMost(lower, name.toLowerCase(), 1) <= 1) return name;
  }
  return undefined;
}
